/**
 * The SPA's end of the tenant change channel (D35): one socket per signed-in
 * page, `changes.subscribe`d, whose `resource.changed` frames invalidate the
 * TanStack queries holding that resource — its list and its detail — so every
 * list refetches over REST (the channel carries ids only; the refetch is where
 * permissions apply) and every open editor hears it (`useLiveResource`,
 * through the change log).
 *
 * `keysForChange` is the whole mapping from a kind to the query keys it
 * touches, one table, unit-tested. A reconnect is a gap — nothing is replayed
 * — so a resumed subscription invalidates every list.
 *
 * The socket's state goes to the connectivity manager (`reportSocket`): this
 * is the one socket a signed-in page always holds, so it is what the panel's
 * WS light shows between editor visits.
 */
import { watchChanges, type ChangesWatchHandle } from '@acp/api-client/changesWatch.js';
import type { SocketFactory, Timers } from '@acp/api-client/ports.js';
import type { ResourceChangeWire, ResourceKindOpenWire } from '@acp/api-contract/changes.js';
import type { ContractParsing } from '@acp/api-contract/parsing.js';
import type { QueryKey } from '@tanstack/react-query';
import type { SocketState } from '../connectivity/state.js';
import type { ChangeLog } from '../lib/live/changeLog.js';
import { alertKeys } from './alerts.js';
import { cloudProductionKeys } from './cloudProductions.js';
import { onboardingKeys } from './onboarding.js';
import { productionKeys } from './productions.js';
import { rundownKeys } from './rundowns.js';
import { showStyleKeys } from './showStyles.js';
import { studioKeys } from './studios.js';

/** Playlists have no key factory of their own (`productions.ts`' `playlistsQuery`). */
const PLAYLISTS: QueryKey = ['playlists'];
/** Keys the admin screens will use; invalidating a key nobody holds costs nothing. */
const SITES: QueryKey = ['sites'];
const NODES: QueryKey = ['nodes'];
const USERS: QueryKey = ['users'];
const GROUPS: QueryKey = ['groups'];
const ME: QueryKey = ['me'];
/** The vendor console (`vendor.ts`): a tenant's change covers the tenants list, its detail and the ops view (the cloud mirrors every tenant's site, node, production and user changes to the vendor tenant as `tenant`). */
const VENDOR_TENANTS: QueryKey = ['vendor', 'tenants'];
const VENDOR_OPS: QueryKey = ['vendor', 'ops'];
const VENDOR_RELEASES: QueryKey = ['vendor', 'releases'];
const VENDOR_ANNOUNCEMENTS: QueryKey = ['vendor', 'announcements'];
const VENDOR_ACTIVITY: QueryKey = ['vendor', 'activity'];
/** The in-app banner's list (`GET /announcements`). */
const ANNOUNCEMENTS: QueryKey = ['announcements'];

/**
 * The query keys (prefixes) a change to a resource of `kind` invalidates: its
 * list and its detail, and the lists that show it inside something else — a
 * rundown inside the playlists list, a show style's name in the studio list,
 * a cloud production in the productions list, a group's grants in `/me`.
 */
export function keysForChange(change: Pick<ResourceChangeWire, 'kind' | 'id'>): readonly QueryKey[] {
  const table: Readonly<Record<ResourceKindOpenWire, readonly QueryKey[]>> = {
    rundown: [rundownKeys.all, PLAYLISTS],
    playlist: [PLAYLISTS, rundownKeys.all],
    studio: [studioKeys.list(), studioKeys.one(change.id), [...showStyleKeys.all, 'validate']],
    showStyle: [showStyleKeys.list(), showStyleKeys.one(change.id), [...showStyleKeys.all, 'validate', change.id], studioKeys.list()],
    production: [productionKeys.list(), productionKeys.one(change.id), productionKeys.asRun(change.id)],
    cloudProduction: [cloudProductionKeys.list(), cloudProductionKeys.one(change.id), productionKeys.list()],
    site: [SITES, NODES],
    node: [NODES, SITES],
    user: [USERS, GROUPS, ME],
    group: [GROUPS, USERS, ME],
    tenant: [VENDOR_TENANTS, VENDOR_OPS, VENDOR_RELEASES, VENDOR_ACTIVITY],
    announcement: [ANNOUNCEMENTS, VENDOR_ANNOUNCEMENTS, VENDOR_ACTIVITY],
    releasePin: [VENDOR_RELEASES, VENDOR_ACTIVITY],
    // Phase 7b: an alert state (its lists, badge counts and history) and the notification settings; the wizard's progress.
    alert: [alertKeys.all],
    alertSettings: [alertKeys.settings()],
    onboarding: [onboardingKeys.all],
    // A kind a newer server announces that this build has no screen for (an open enum, D37): nothing here shows it.
    unknown: [],
  };
  return table[change.kind];
}

/** Every list: what a resumed subscription (a gap) refetches. */
export const LIST_KEYS: readonly QueryKey[] = [rundownKeys.all, PLAYLISTS, studioKeys.all, showStyleKeys.all, productionKeys.all, cloudProductionKeys.all, SITES, NODES, USERS, GROUPS, ['vendor'], ANNOUNCEMENTS, alertKeys.all, onboardingKeys.all];

/**
 * The kinds a node's own change channel announces: what it replicates and serves (`RouteDef.hosts` both). Everything
 * else — sites, nodes, users, groups, alerts, cloud productions, the vendor's — only the cloud's channel announces, so a
 * node-served page that shows those screens (their calls go to the cloud: `connectivity/routing.ts`) also listens to
 * the cloud's channel, for those kinds only (`useLiveChanges`).
 */
export const NODE_ANNOUNCED_KINDS: ReadonlySet<string> = new Set(['rundown', 'playlist', 'studio', 'showStyle', 'production']);

/** A change only the cloud's channel announces (an open enum's `unknown` included: a newer cloud's kind is its own). */
export function cloudOnlyKind(kind: ResourceKindOpenWire): boolean {
  return !NODE_ANNOUNCED_KINDS.has(kind);
}

/** The lists of the cloud-only kinds: what a resumed cloud subscription refetches. */
export const CLOUD_ONLY_LIST_KEYS: readonly QueryKey[] = [cloudProductionKeys.all, SITES, NODES, USERS, GROUPS, ME, ['vendor'], ANNOUNCEMENTS, alertKeys.all, onboardingKeys.all];

export type ChangeFeedDeps = {
  /** Read at every connect: the current primary's socket. */
  readonly wsUrl: () => string;
  readonly tenantId: string;
  readonly token: () => Promise<string>;
  readonly openSocket: SocketFactory;
  readonly timers: Timers;
  readonly nowMs: () => number;
  readonly parsing: ContractParsing;
  readonly reportSocket: (state: SocketState) => void;
  /** The query client's invalidation (a prefix match: a list key covers its pages). */
  readonly invalidate: (queryKey: QueryKey) => Promise<void>;
  readonly log: ChangeLog;
  /** Which kinds this socket's changes apply for (the primary's: all; the cloud's beside a node primary: `cloudOnlyKind`). */
  readonly accept: (kind: ResourceKindOpenWire) => boolean;
  /** What a resumed subscription (a gap) refetches. */
  readonly resumeKeys: readonly QueryKey[];
};

/**
 * The cloud's change feed beside a node primary (host-aware routing): the primary feed's deps, pointed at the cloud's
 * socket with the session's own (Cognito) token — never a node's local session — applying only the kinds the node's
 * channel does not announce, not lighting the panel's socket (that is the primary's).
 */
export function besideNodeFeed(base: ChangeFeedDeps, cloudWsUrl: string, idToken: (force: boolean) => Promise<string>): ChangeFeedDeps {
  return { ...base, wsUrl: () => cloudWsUrl, token: () => idToken(false), reportSocket: () => undefined, accept: cloudOnlyKind, resumeKeys: CLOUD_ONLY_LIST_KEYS };
}

export type ChangeFeedSession = {
  readonly start: () => void;
  readonly stop: () => void;
  /** The primary moved (a failover, the operator's switch): resubscribe there. */
  readonly setPrimary: (primaryId: string) => void;
};

export function createChangeFeedSession(deps: ChangeFeedDeps): ChangeFeedSession {
  let handle: ChangesWatchHandle | null = null;
  let primary: string | null = null;
  const invalidateAll = (keys: readonly QueryKey[]): void => {
    for (const key of keys) void deps.invalidate(key);
  };
  return {
    start() {
      if (handle !== null) return;
      const started = watchChanges({
        wsUrl: deps.wsUrl,
        tenantId: deps.tenantId,
        token: deps.token,
        openSocket: deps.openSocket,
        timers: deps.timers,
        nowMs: deps.nowMs,
        parsing: deps.parsing,
        onEvent: (event) => {
          switch (event.type) {
            case 'change':
              if (!deps.accept(event.change.kind)) return;
              deps.log.publish(event.change, event.ts);
              invalidateAll(keysForChange(event.change));
              return;
            case 'subscribed':
              deps.reportSocket('open');
              // A reconnect is a gap: whatever changed meanwhile was not heard.
              if (event.resumed) invalidateAll(deps.resumeKeys);
              return;
            case 'connecting':
            case 'disconnected':
              deps.reportSocket('connecting');
              return;
            case 'nack':
            case 'warning':
              return;
          }
        },
      });
      handle = started;
      started.done.catch(() => {
        // No token (signed out mid-flight): the layout unmounts and stops this session.
        if (handle === started) deps.reportSocket('closed');
      });
    },
    stop() {
      handle?.stop();
      handle = null;
      deps.reportSocket('unknown');
    },
    setPrimary(primaryId) {
      if (primary !== null && primary !== primaryId) handle?.reconnect();
      primary = primaryId;
    },
  };
}
